import { FocusEventHandler, memo, useCallback, useMemo } from 'react';

import IconButton from '@material-ui/core/IconButton';
import clsx from 'clsx';

import { MinusIcon, PlusIcon, colors } from '../../assets';
import { Input } from '../input/Input';
import { useStyles } from './IncrementInput.styles';
import { IncrementInputMask, IncrementInputProps } from './IncrementInput.types';

export const IncrementInput = memo(
  ({
    value,
    step = 1,
    mask,
    min,
    max,
    className,
    inputRef,
    classes,
    name,
    error,
    size,
    disabled,
    id,
    onChange,
    onValidation,
    fillEmpty,
    allowOutOfRange,
    defaultNumericValue = null,
    ...props
  }: IncrementInputProps) => {
    const styles = useStyles({ size });

    const calculatedMask = useMemo<IncrementInputMask>(() => {
      if (mask) {
        return mask;
      }

      if (min !== undefined && min >= 0) {
        return 'positive-integer';
      }

      return 'integer';
    }, [mask, min]);

    /**
     * The value as a number, for the bound checks that disable the stepper buttons.
     * An empty or unparseable field has no number to compare, so it falls back to
     * `defaultNumericValue` — `null` there leaves both buttons enabled.
     */
    const numericValue = useMemo(() => {
      if (value === null || value === undefined || (value as unknown) === '') {
        return defaultNumericValue;
      }

      const parsed = Number(value);

      return Number.isNaN(parsed) ? defaultNumericValue : parsed;
    }, [defaultNumericValue, value]);

    // Compared with <= / >= rather than equality: a value handed in already past a bound
    // must not be pushed further out. With clamping on the two are equivalent, since the
    // value can then never sit outside the range in the first place.
    const decrementDisabled = useMemo(
      () => disabled || (min !== undefined && numericValue !== null && numericValue <= min),
      [disabled, min, numericValue],
    );

    const incrementDisabled = useMemo(
      () => disabled || (max !== undefined && numericValue !== null && numericValue >= max),
      [disabled, max, numericValue],
    );

    const validate = useCallback(
      (value: number | null) => {
        if (!value) {
          return;
        }
        const minError = min !== undefined && value < min;
        const maxError = max !== undefined && value > max;
        const stepError = value % step !== 0;
        const isValid = !stepError && !minError && !maxError;
        const validationMessage = isValid ? '' : `Please enter valid number (step:${step}, min:${step}, max:${max})`;
        onValidation && onValidation(validationMessage);

        return validationMessage;
      },
      [max, min, onValidation, step],
    );

    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
      e => {
        if (fillEmpty && (!e.target.value || e.target.value === '')) {
          onChange(min || 0);
        }
      },
      [fillEmpty, min, onChange],
    );

    const handleValueChange = useCallback(
      (inputValue: string) => {
        const currentValue = inputValue !== '' ? Number.parseFloat(inputValue) : null;
        validate(currentValue);

        // With allowOutOfRange the typed number reaches the consumer as-is, so its own
        // validation can report the problem rather than this input quietly rewriting it.
        if (!allowOutOfRange) {
          if (max !== undefined && currentValue) {
            if (currentValue > max) {
              onChange(max);

              return;
            }
          }
          if (min !== undefined && currentValue) {
            if (currentValue < min) {
              onChange(min);

              return;
            }
          }
        }
        onChange(currentValue);
      },
      [allowOutOfRange, max, min, onChange, validate],
    );

    const handleStepChange = useCallback(
      (step: number) => {
        const currentValue = value ? value + step : step;

        // Under allowOutOfRange a value already outside the range steps back towards it
        // one increment at a time, rather than snapping to the bound in a single click.
        // The buttons below are disabled at the bounds, so stepping cannot leave the range.
        if (!allowOutOfRange) {
          if (min !== undefined && min > currentValue) {
            onChange(min);

            return;
          }

          if (max !== undefined && max < currentValue) {
            onChange(max);

            return;
          }
        }

        onChange(currentValue);
      },
      [allowOutOfRange, max, min, onChange, value],
    );

    return (
      <div className={clsx(styles.container, className)} data-testid={props['data-testid']}>
        <div className={clsx(classes?.buttonWrapper)}>
          <IconButton
            className={clsx(styles.button, decrementDisabled && styles.buttonDisabled, classes?.button)}
            onClick={() => handleStepChange(-step)}
            id={`${name}-decrement`}
            size={size}
            disabled={decrementDisabled}
            aria-label="Decrement"
          >
            <MinusIcon color={decrementDisabled ? colors.grey120 : colors.secondary.main} />
          </IconButton>
        </div>
        <div className={clsx(classes?.inputWrapper, styles.inputWrapper, error && styles.error)}>
          <Input
            id={id || name}
            name={name}
            className={styles.rootInput}
            mask={calculatedMask}
            color="primary"
            variant="outlined"
            inputRef={inputRef}
            value={value ?? ''}
            onChange={e => handleValueChange(e.target.value)}
            onBlur={handleBlur}
            size={size}
            disabled={disabled}
            {...props}
          />
        </div>
        <div className={clsx(classes?.buttonWrapper)}>
          <IconButton
            className={clsx(styles.button, incrementDisabled && styles.buttonDisabled, classes?.button)}
            onClick={() => handleStepChange(step)}
            id={`${name}-increment`}
            size={size}
            disabled={incrementDisabled}
            aria-label="Increment"
          >
            <PlusIcon color={incrementDisabled ? colors.grey120 : colors.secondary.main} />
          </IconButton>
        </div>
      </div>
    );
  },
);
